// ---------------------------------------------------
//              Employee Pending Types
// ---------------------------------------------------

export interface PendingEmployee {
    firstName?: string;
    lastName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
    gender?: number;
    username?: string;
    dateOfBirth?: string;
    phoneNumber?: string;
    employeeRole?: number;
}

export interface PendingUpdateEmployee {
    employeeId?: number;
    field?: string;
    value?: any;
}

export interface PendingDeleteEmployee {
    awaitingName?: boolean;
}

export const startCreateEmployeeFlow = (
    setPendingEmployee: (data: PendingEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    firstNameFromIntent?: string
) => {
    if (firstNameFromIntent) {
        setPendingEmployee({ firstName: firstNameFromIntent });
        pushAIChat(`Got it. What is ${firstNameFromIntent}'s last name?`);
        speak(`What is ${firstNameFromIntent}'s last name?`);
        return;
    }

    setPendingEmployee({});
    pushAIChat("Sure! What is the employee's first name?");
    speak("What is the employee's first name?");
};

export const processPendingEmployee = (
    text: string,
    pendingEmployee: PendingEmployee,
    setPendingEmployee: (data: PendingEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    if (!pendingEmployee.firstName) {
        setPendingEmployee({ ...pendingEmployee, firstName: text });
        pushAIChat("Great! What is the last name?");
        speak("What is the last name?");
        return;
    }

    if (!pendingEmployee.lastName) {
        setPendingEmployee({ ...pendingEmployee, lastName: text });
        pushAIChat("What is the email address?");
        speak("What is the email address?");
        return;
    }

    if (!pendingEmployee.email) {
        setPendingEmployee({ ...pendingEmployee, email: text });
        pushAIChat("Set a password for the employee.");
        speak("Please provide a password.");
        return;
    }

    if (!pendingEmployee.password) {
        setPendingEmployee({ ...pendingEmployee, password: text });
        pushAIChat("Please confirm the password.");
        speak("Please confirm the password.");
        return;
    }

    if (!pendingEmployee.confirmPassword) {
        setPendingEmployee({ ...pendingEmployee, confirmPassword: text });
        pushAIChat("What is the gender? Say male or female.");
        speak("What is the gender?");
        return;
    }

    if (!pendingEmployee.gender) {
        const gender = text.includes("female") ? 2 : 1;
        setPendingEmployee({ ...pendingEmployee, gender });
        pushAIChat("What should be the username?");
        speak("What should be the username?");
        return;
    }

    if (!pendingEmployee.username) {
        setPendingEmployee({ ...pendingEmployee, username: text });
        pushAIChat("What is the date of birth? (YYYY-MM-DD)");
        speak("Please provide date of birth.");
        return;
    }

    if (!pendingEmployee.dateOfBirth) {
        setPendingEmployee({ ...pendingEmployee, dateOfBirth: text });
        pushAIChat("What is the phone number?");
        speak("Please provide phone number.");
        return;
    }

    if (!pendingEmployee.phoneNumber) {
        setPendingEmployee({ ...pendingEmployee, phoneNumber: text });

        pushAIChat("What is the employee role?");
        speak("What is the employee role?");

        //  SHOW ROLE SUGGESTIONS
        window.dispatchEvent(new CustomEvent("ai-suggest-options", {detail: { type: "role" }}));
        return;
    }
};

export const startUpdateEmployeeFlow = (
    setPendingUpdate: (data: PendingUpdateEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    employeeId?: number
) => {
    if (!employeeId) {
        pushAIChat("Please tell me the employee ID you want to update.");
        speak("Which employee do you want to update?");
        return;
    }

    setPendingUpdate({ employeeId });
    pushAIChat("What do you want to update?");
    speak("What field should I update?");
};

export const processPendingUpdateEmployee = (
    text: string,
    pendingUpdate: PendingUpdateEmployee,
    setPendingUpdate: (data: PendingUpdateEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    if (!pendingUpdate.field) {
        setPendingUpdate({ ...pendingUpdate, field: text });
        pushAIChat(`What should be the new value for ${text}?`);
        speak(`Please provide the new value.`);
        return;
    }

    pushAIChat("Updating employee...");
    speak("Updating employee.");

    window.dispatchEvent(
        new CustomEvent("ai-update-employee", {
            detail: {
                employeeId: pendingUpdate.employeeId,
                [pendingUpdate.field]: text
            }
        })
    );

    setPendingUpdate(null);
};

export const startDeleteEmployeeFlow = (
    setPendingDelete: (data: PendingDeleteEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting employee "${nameFromIntent}"...`);
        speak(`Deleting employee ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-employee", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which employee do you want to delete?");
    speak("Which employee should I delete?");
};

export const processPendingDeleteEmployee = (
    text: string,
    pendingDelete: PendingDeleteEmployee,
    setPendingDelete: (data: PendingDeleteEmployee | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    if (pendingDelete.awaitingName) {
        const cleanName = text.replace(/[^a-zA-Z ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid employee name.");
            speak("Please say a valid name.");
            return;
        }

        pushAIChat(`Deleting employee "${cleanName}"...`);
        speak(`Deleting employee ${cleanName}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-employee", {
                detail: { name: cleanName }
            })
        );

        setPendingDelete(null);
    }
};

